import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from "lucide-react";

const API_BASE = `${import.meta.env.BASE_URL}api`;

const FINAL_STATUSES = ["confirmed", "cancelled", "payment_failed", "payment_expired"] as const;
type FinalStatus = (typeof FINAL_STATUSES)[number];

type BookingConfirmDTO = {
  id: string;
  createdAt: string;
  name: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  extras: string[];
  totalAmount: number;
  // optional fields if backend includes them
  chaletId?: string;
  chalet_id?: string;
  extraLabels?: string[];
};

export const Confirmation: React.FC = () => {
  // ✅ Works with HashRouter URLs like: #/confirmation?bookingId=22&token=abc
  const queryParams = useMemo(() => {
    const hash = window.location.hash;
    const query = hash.includes("?") ? hash.split("?")[1] : "";
    return new URLSearchParams(query);
  }, []);

  const bookingId = useMemo(() => {
    const raw = queryParams.get("bookingId");
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  }, [queryParams]);

  const token = useMemo(() => {
    const t = queryParams.get("token");
    return t && t.trim().length > 0 ? t.trim() : null;
  }, [queryParams]);

  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // ✅ Booking details
  const [booking, setBooking] = useState<BookingConfirmDTO | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // 1) Fetch confirmation details (token-protected)
  useEffect(() => {
    if (!bookingId || !token) {
      setDetailsError(!bookingId ? "Missing bookingId" : "Missing token");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setDetailsLoading(true);
        setDetailsError(null);

        const url = `${API_BASE}/bookings/get_confirmation.php?bookingId=${encodeURIComponent(
          String(bookingId)
        )}&token=${encodeURIComponent(token)}`;

        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (cancelled) return;

        if (res.status === 404) {
          setNotFound(true);
          setBooking(null);
          return;
        }

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.success || !data?.booking) {
          setDetailsError(data?.error || `HTTP ${res.status}`);
          setBooking(null);
          return;
        }

        setNotFound(false);
        {
          const b: any = data.booking;
          const normalized: BookingConfirmDTO = {
            ...b,
            // support multiple backend field names
            chaletId: b?.chaletId ?? b?.chalet_id ?? b?.apartment_id ?? undefined,
            chalet_id: b?.chalet_id ?? undefined,
            extraLabels: b?.extraLabels ?? b?.extra_labels ?? undefined,
          };
          setBooking(normalized);
        }
      } catch (e: any) {
        if (cancelled) return;
        setDetailsError(e?.message || "Failed to load booking");
        setBooking(null);
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, token]);

  // 2) Keep your existing status polling (works even without token)
  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let isCancelled = false;
    let tries = 0;

    const maxTries = 20; // ~30 sec
    const intervalMs = 1500;

    const poll = async () => {
      tries += 1;

      try {
        const url = `${API_BASE}/bookings/getstatus.php?id=${bookingId}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });

        if (isCancelled) return;

        if (res.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setNotFound(false);

        const s = typeof data?.status === "string" ? data.status.trim().toLowerCase() : null;
        setStatus(s);

        if (s && (FINAL_STATUSES as readonly string[]).includes(s)) {
          setLoading(false);
          return;
        }

        if (tries >= maxTries) {
          setLoading(false);
          setTimedOut(true);
          return;
        }

        setTimeout(poll, intervalMs);
      } catch {
        if (isCancelled) return;

        if (tries >= maxTries) {
          setLoading(false);
          setTimedOut(true);
          return;
        }

        setTimeout(poll, intervalMs);
      }
    };

    setLoading(true);
    poll();

    return () => {
      isCancelled = true;
    };
  }, [bookingId]);

  const ui = useMemo(() => {
    if (notFound) {
      return {
        iconWrap: "bg-stone-100 text-stone-700",
        Icon: AlertTriangle,
        title: "Boeking niet gevonden",
        text:
          "De booking kon niet worden gevonden. Controleer de link of neem contact op.",
        primary: { label: "Terug naar home", to: "/" },
      };
    }

    if ((loading && !status) || (status === "pending_payment" && !timedOut)) {
      return {
        iconWrap: "bg-stone-100 text-stone-700",
        Icon: Loader2,
        iconClassName: "animate-spin",
        title: "Betaling in verwerking…",
        text:
          "We wachten op bevestiging van Mollie. Dit duurt meestal maar een paar seconden. Ververs de pagina als het te lang duurt.",
        primary: { label: "Terug naar home", to: "/" },
      };
    }

    if (timedOut && status === "pending_payment") {
      return {
        iconWrap: "bg-amber-100 text-amber-700",
        Icon: AlertTriangle,
        title: "Bevestiging niet ontvangen",
        text:
          "We hebben nog geen bevestiging ontvangen van Mollie. Als je al betaald hebt, ververs dan de pagina over een minuut. Neem anders contact op.",
        primary: { label: "Pagina verversen", to: `/#/confirmation?bookingId=${bookingId ?? ""}${token ? `&token=${token}` : ""}` },
      };
    }

    if (status === "confirmed") {
      return {
        iconWrap: "bg-green-100 text-green-600",
        Icon: CheckCircle,
        title: "Bedankt voor je boeking!",
        text:
          "Je betaling is geslaagd. Hieronder vind je de belangrijkste details van je boeking.",
        primary: { label: "Terug naar home", to: "/" },
      };
    }

    if (status === "cancelled") {
      return {
        iconWrap: "bg-stone-100 text-stone-700",
        Icon: XCircle,
        title: "Betaling geannuleerd",
        text: "Je hebt de betaling geannuleerd. De boeking is niet bevestigd. Je kunt het opnieuw proberen.",
        primary: { label: "Opnieuw proberen", to: "/" },
      };
    }

    if (status === "payment_failed") {
      return {
        iconWrap: "bg-amber-100 text-amber-700",
        Icon: AlertTriangle,
        title: "Betaling mislukt",
        text:
          "De betaling is mislukt. Probeer het opnieuw. Als dit blijft gebeuren, kies een andere betaalmethode.",
        primary: { label: "Opnieuw proberen", to: "/" },
      };
    }

    if (status === "payment_expired") {
      return {
        iconWrap: "bg-amber-100 text-amber-700",
        Icon: AlertTriangle,
        title: "Betaling verlopen",
        text: "De betaling is verlopen. Probeer het opnieuw.",
        primary: { label: "Opnieuw proberen", to: "/" },
      };
    }

    return {
      iconWrap: "bg-stone-100 text-stone-700",
      Icon: AlertTriangle,
      title: "Onbekende status",
      text: `Status: ${status ?? "onbekend"}`,
      primary: { label: "Terug naar home", to: "/" },
    };
  }, [loading, status, notFound, timedOut, bookingId, token]);

  const Icon = ui.Icon;

const [extrasMap, setExtrasMap] = useState<Record<string, string>>({});

// 3) Resolve extras ids -> labels (extras are per chalet in your Admin flow)
useEffect(() => {
  const chaletId = (booking as any)?.chaletId ?? (booking as any)?.chalet_id ?? null;
  if (!chaletId) return;

  let cancelled = false;

  (async () => {
    try {
      const res = await fetch(
        `${API_BASE}/extras/list.php?chalet_id=${encodeURIComponent(String(chaletId))}`,
        { headers: { Accept: "application/json" } }
      );

      if (!res.ok) return;

      const rows: any[] = await res.json().catch(() => []);
      if (cancelled) return;

      const map: Record<string, string> = {};
      for (const r of rows) {
        const id = String(r?.id ?? "").trim();
        const label = String(r?.label ?? "").trim();
        if (id) map[id] = label || id;
      }
      setExtrasMap(map);
    } catch {
      // silent: extras labels are nice-to-have
    }
  })();

  return () => {
    cancelled = true;
  };
}, [(booking as any)?.chaletId, (booking as any)?.chalet_id]);


  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 text-center">
      <div className={`w-24 h-24 ${ui.iconWrap} rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500`}>
        <Icon size={48} className={(ui as any).iconClassName ?? ""} />
      </div>

      <h1 className="text-4xl font-serif font-bold text-brand-900 mb-4">{ui.title}</h1>
      <p className="text-stone-600 max-w-md mb-8 text-lg">{ui.text}</p>

      {bookingId && (
        <p className="text-stone-400 text-sm mb-4">
          Boekingnummer: {bookingId}
        </p>
      )}

      {/* ✅ Booking details panel (token-protected) */}
      {detailsLoading && (
        <div className="text-stone-500 text-sm mb-6 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Boekingsdetails laden…
        </div>
      )}

      {detailsError && !detailsLoading && detailsError !== "Invalid token" && detailsError !== "Token expired" && (
        <div className="text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-sm mb-6">
          Kan boekingsdetails niet laden: {detailsError}
        </div>
      )}

      {booking && (
        <div className="w-full max-w-lg bg-white border border-brand-100 rounded-2xl shadow-sm p-6 mb-8 text-left">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Naam</div>
              <div className="font-semibold text-stone-800">{booking.name}</div>
            </div>
            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Aangemaakt op</div>
              <div className="font-semibold text-stone-800">{booking.createdAt}</div>
            </div>

            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Check-in</div>
              <div className="font-semibold text-stone-800">{booking.checkIn}</div>
            </div>
            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Check-out</div>
              <div className="font-semibold text-stone-800">{booking.checkOut}</div>
            </div>

            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Nachten</div>
              <div className="font-semibold text-stone-800">{booking.nights}</div>
            </div>
            <div>
              <div className="text-stone-400 text-xs font-bold uppercase">Totaal</div>
              <div className="font-semibold text-stone-800">€{booking.totalAmount.toFixed(2)}</div>
            </div>

            <div className="col-span-2">
              <div className="text-stone-400 text-xs font-bold uppercase">Extra’s</div>
				<div className="font-semibold text-stone-800">
				  {booking.extraLabels?.length
					? booking.extraLabels.join(", ")
					: booking.extras?.length
					  ? booking.extras.join(", ")
					  : "Geen"}
				</div>
            </div>
          </div>
        </div>
      )}

      <Link
        to={ui.primary.to}
        className="px-8 py-3 bg-brand-800 text-white rounded-xl font-medium hover:bg-brand-700 transition-all shadow-lg hover:shadow-xl"
      >
        {ui.primary.label}
      </Link>
    </div>
  );
};
